import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Link2, MousePointer, Activity, TrendingUp, Plus, ExternalLink, Info, List } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StatsCard } from '../components/StatsCard'
import { linkTrackerApi } from '../api'
import type { DashboardStats } from '../types'

// Tipos para stats individuales
interface CardStats {
  totalURLs: number
  urlsActivas: number
  totalClics: number
  clicsHoy: number
}

export function DashboardLinkTracker() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [cardStats, setCardStats] = useState<CardStats>({
    totalURLs: 0,
    urlsActivas: 0,
    totalClics: 0,
    clicsHoy: 0
  })
  const [loading, setLoading] = useState(true)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    cargarDatos()
  }, [])

  const cargarDatos = async () => {
    try {
      // Cargar stats de cards y dashboard en paralelo
      const [
        totalURLsRes,
        urlsActivasRes,
        totalClicsRes,
        clicsHoyRes,
        dashboardRes
      ] = await Promise.all([
        linkTrackerApi.getTotalURLs(),
        linkTrackerApi.getURLsActivas(),
        linkTrackerApi.getTotalClics(),
        linkTrackerApi.getClicsHoy(),
        linkTrackerApi.getDashboardStats()
      ])

      setCardStats({
        totalURLs: totalURLsRes.total_urls,
        urlsActivas: urlsActivasRes.urls_activas,
        totalClics: totalClicsRes.total_clics,
        clicsHoy: clicsHoyRes.clics_hoy
      })
      setStats(dashboardRes)
    } catch (error) {
      console.error('Error cargando datos:', error)
      toast.error('Error al cargar estadísticas')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <p>Error al cargar datos</p>
        <Button variant="link" onClick={cargarDatos}>Reintentar</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Link2 className="h-6 w-6" />
            Link Tracker
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              URLs acortadas con tracking de clics
            </span>
          )}
        </div>
        <Button asChild className="bg-teal-800 hover:bg-teal-900 text-white">
          <Link to="/link-tracker/crear">
            <Plus className="h-4 w-4 mr-2" />
            Nueva URL
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total URLs"
          value={cardStats.totalURLs}
          icon={Link2}
        />
        <StatsCard
          title="URLs Activas"
          value={cardStats.urlsActivas}
          icon={Activity}
        />
        <StatsCard
          title="Total Clics"
          value={cardStats.totalClics}
          icon={MousePointer}
        />
        <StatsCard
          title="Clics Hoy"
          value={cardStats.clicsHoy}
          icon={TrendingUp}
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top URLs */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">URLs más clickeadas</CardTitle>
            <Button variant="outline" size="sm" className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800" asChild>
              <Link to="/link-tracker/urls">
                <List className="h-4 w-4 mr-1" />
                Ver todas
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {stats.top_urls.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No hay URLs todavía
              </p>
            ) : (
              <div className="space-y-3">
                {stats.top_urls.slice(0, 5).map((url, index) => (
                  <div key={url.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-muted-foreground w-4">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {url.titulo || url.codigo}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          /c/{url.codigo}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="badge-gradient hover:opacity-90">{url.total_clics} clics</Badge>
                      <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                        <Link to={`/link-tracker/analytics/${url.codigo}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clics Recientes */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Clics recientes</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.clics_recientes.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                No hay clics todavía
              </p>
            ) : (
              <div className="space-y-3">
                {stats.clics_recientes.slice(0, 10).map((clic) => (
                  <div key={clic.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge className="badge-gradient hover:opacity-90 font-mono text-xs shrink-0">
                        {clic.codigo_url}
                      </Badge>
                      <span className="text-muted-foreground truncate">
                        {clic.numero_destinatario || clic.ip_address || 'Anónimo'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className="badge-gradient hover:opacity-90 text-xs">
                        {clic.dispositivo}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(clic.fecha_clic).toLocaleTimeString('es-EC', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Clics por Canal */}
      {stats.clics_por_canal.length > 0 && (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Clics por canal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {stats.clics_por_canal.map((item) => (
                <div key={item.canal} className="flex items-center gap-2">
                  <Badge className="badge-gradient hover:opacity-90 capitalize">
                    {item.canal}
                  </Badge>
                  <span className="font-semibold">{item.total}</span>
                  <span className="text-muted-foreground text-sm">clics</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
